import { FileImageOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Col, Form, Layout, PageHeader, Row, Space } from "antd";
import Dragger from "antd/lib/upload/Dragger";
import { useApplicationContext } from "components/Application";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import update from "immutability-helper";
import React, { Fragment, useCallback, useRef, useState } from "react";
import { createDndContext, DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { NavLink, useNavigate, useParams } from "react-router-dom";

const articleItemQuery = gql`
  query($id: ID!) {
    articleItem(id: $id) {
      id
      images {
        id
        image
        position
      }
    }
  }
`;

const updateImagesMutation = gql`
  mutation($input: UpdateImagesInput) {
    updateImages(input: $input) {
      results {
        successful
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const Edit: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();

  const [fileList, setFileList] = useState([]);
  const [toDelete, setToDelete] = useState([]);

  const { error, loading: queryLoading } = useQuery(articleItemQuery, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    onCompleted: (data: any) => {
      setFileList(
        data.articleItem?.images.map((image: any, i: any) => ({
          uid: i,
          id: image.id,
          url: `${appEndpointStore}${image.image}`,
        })),
      );
    },
  });

  const [execMutation, { loading }] = useMutation(updateImagesMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: graphQLError.name, errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate("..");
    },
  });

  const onFinish = () => {
    const input: any = [];
    fileList.forEach((image: any, i) => {
      if (image.originFileObj) {
        // new image to upload
        const fd = new FormData();
        input.push({
          itemId: id,
          position: i,
          image: (fd.append("file", image.originFileObj), fd.get("file")),
        });
      } else
        input.push({
          id: image.id,
          position: i,
        });
    });
    toDelete.forEach((id: any) => input.push({ id, delete: true }));

    execMutation({ variables: { input } });
  };

  const type = "DragableUploadList";
  const DragableUploadListItem = ({ originNode, moveRow, file, fileList }: any) => {
    const ref = useRef();
    const index = fileList.indexOf(file);
    const [{ isOver, dropClassName }, drop] = useDrop({
      accept: type,
      collect: (monitor: any) => {
        const { index: dragIndex } = monitor.getItem() || {};
        if (dragIndex === index) {
          return {};
        }
        return {
          isOver: monitor.isOver(),
          dropClassName: dragIndex < index ? " drop-over-downward" : " drop-over-upward",
        };
      },
      drop: (item: any) => {
        moveRow(item.index, index);
      },
    });
    const [, drag] = useDrag({
      item: { type, index },
      collect: (monitor: any) => ({
        isDragging: monitor.isDragging(),
      }),
    });
    drop(drag(ref));
    return (
      <div
        ref={ref}
        className={`ant-upload-draggable-list-item ${isOver ? dropClassName : ""}`}
        style={{ cursor: "move" }}
      >
        {originNode}
      </div>
    );
  };

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = fileList[dragIndex];
      setFileList(
        update(fileList, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        }),
      );
    },
    [fileList],
  );

  const RNDContext = createDndContext(HTML5Backend);
  const manager = useRef(RNDContext);

  const onChange = ({ file, fileList: newFileList }: any) => {
    if (file.status === "removed" && file.id)
      setToDelete(
        update(toDelete, {
          $push: [file.id],
        }),
      );

    setFileList(newFileList);
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="..">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const uploadProps = {
    multiple: true,
    maxCount: 25,
    accept: "image/*",
    listType: "picture-card",
    beforeUpload: () => {
      return false;
    },
  };

  return (
    <Fragment>
      <PageHeader
        ghost={true}
        title="Edit Images"
        extra={
          <Row gutter={[24, 0]}>
            <Col>
              <NavLink to="..">
                <Button>Cancel</Button>
              </NavLink>
            </Col>
            <Col>
              <Button
                type="primary"
                loading={loading}
                onClick={() => {
                  form.submit();
                }}
              >
                Save
              </Button>
            </Col>
          </Row>
        }
      />

      <Layout.Content className="content">
        {otherErrors.length > 0 && (
          <Space direction="vertical" style={{ marginBottom: "10px" }}>
            {otherErrors.map((otherError, index) => (
              <Alert key={index} type="error" message="Error" description={otherError} />
            ))}
          </Space>
        )}
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Form.Item name="images" label="Choose up to 25 images and drag them to sort">
                <DndProvider manager={manager.current.dragDropManager}>
                  <Dragger
                    {...uploadProps}
                    fileList={fileList}
                    onChange={onChange}
                    itemRender={(originNode, file, currFileList) => (
                      <DragableUploadListItem
                        originNode={originNode}
                        file={file}
                        fileList={currFileList}
                        moveRow={moveRow}
                      />
                    )}
                  >
                    <div>
                      <FileImageOutlined style={{ fontSize: "30px" }} />
                      <div style={{ marginTop: 8 }}>Click or drop here</div>
                    </div>
                  </Dragger>
                </DndProvider>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Layout.Content>
    </Fragment>
  );
};
